/**
 * Member Stats Modal Component
 * Displays detailed trade statistics for a specific member
 */

import React from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useColorModeValue,
  Text,
  VStack,
  HStack,
  SimpleGrid,
  Box,
  Divider,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
} from "@chakra-ui/react";
import { Member } from "./MembersTable";
import TradeLogMiniStatistics from "../../TradeLog/components/TradeLogMiniStatistics";
import { useDisplayCurrency } from "../../../../contexts/DisplayCurrencyContext";

export interface MemberStats {
  member: Member;
  // Performance Overview
  winRate: number;
  roi: number;
  activeTrades: number;
  // Trading Activity
  closedTrades: number;
  tradesThisMonth: number;
  averageTradeSize: number;
  // Performance Metrics
  bestTrade: {
    symbol: string;
    profit: number;
    date: string;
  };
  worstTrade: {
    symbol: string;
    loss: number;
    date: string;
  };
  averageWin: number;
  averageLoss: number;
  winLossRatio: number;
  currentStreak: {
    type: "win" | "loss";
    count: number;
  };
  longestWinStreak: number;
  longestLossStreak: number;
  // Portfolio Metrics
  totalInvested: number;
  totalReturn: number;
  profitFactor: number;
  sharpeRatio: number | null;
  maxDrawdown: number;
  // Engagement
  brokerConnected: boolean;
  location: string;
  accountAge: number; // days
  // Recent Trades
  recentTrades: Array<{
    id: string;
    symbol: string;
    date: string;
    pnl: number;
    returnPercent: number;
  }>;
}

interface MemberStatsModalProps {
  isOpen: boolean;
  onClose: () => void;
  stats: MemberStats | null;
}

const MemberStatsModal: React.FC<MemberStatsModalProps> = ({
  isOpen,
  onClose,
  stats,
}) => {
  const { formatMoney } = useDisplayCurrency();
  const textColor = useColorModeValue("#575757", "white");
  const cardBg = useColorModeValue("white", "#151517");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const secondaryTextColor = useColorModeValue("gray.400", "gray.400");

  // Format percent
  const formatPercent = (value: number): string => {
    return `${value >= 0 ? "+" : ""}${value.toFixed(2)}%`;
  };

  // Format date
  const formatDate = (date: string): string => {
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(new Date(date));
  };

  if (!stats) return null;

  const { member } = stats;
  const isProfit = stats.totalReturn > 0;
  const isLoss = stats.totalReturn < 0;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
      <ModalContent bg={cardBg} maxH="90vh">
        <ModalHeader
          pt={6}
          px={6}
          pb={4}
          borderBottom="1px solid"
          borderColor={borderColor}
        >
          <VStack align="start" spacing={2}>
            <Text
              fontSize="2xl"
              fontWeight="500"
              color="#38BBD1"
              fontFamily="'Urbanist', sans-serif"
            >
              {member.name}
            </Text>
            <Text
              fontSize="sm"
              color={secondaryTextColor}
              fontFamily="'IBM Plex Sans', sans-serif"
            >
              {member.email}
            </Text>
            <HStack spacing={3}>
              <Text
                fontSize="xs"
                color={secondaryTextColor}
                fontFamily="'IBM Plex Sans', sans-serif"
              >
                Joined: {formatDate(member.joinedDate)}
              </Text>
              <Text fontSize="xs" color={secondaryTextColor}>
                •
              </Text>
              <Text
                fontSize="xs"
                color={secondaryTextColor}
                fontFamily="'IBM Plex Sans', sans-serif"
              >
                Account Age: {stats.accountAge} days
              </Text>
              <Text fontSize="xs" color={secondaryTextColor}>
                •
              </Text>
              <Text
                fontSize="xs"
                color={secondaryTextColor}
                fontFamily="'IBM Plex Sans', sans-serif"
              >
                Location: {stats.location}
              </Text>
            </HStack>
          </VStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody p={6}>
          <VStack spacing={6} align="stretch">
            {/* Performance Overview Cards */}
            <Box>
              <Text
                fontSize="xs"
                color={secondaryTextColor}
                fontWeight="medium"
                fontFamily="'IBM Plex Sans', sans-serif"
                textTransform="uppercase"
                mb={4}
              >
                Performance Overview
              </Text>
              <SimpleGrid columns={{ base: 1, sm: 2, md: 4 }} spacing={4}>
                <TradeLogMiniStatistics
                  title="Total P&L"
                  amount={formatMoney(stats.totalReturn)}
                  percentage={stats.roi}
                  backgroundGradient={
                    isProfit
                      ? "linear-gradient(135deg, #06E363, #AAFAB1)"
                      : isLoss
                      ? "linear-gradient(135deg, #E00101, #FE4244)"
                      : "linear-gradient(135deg, #38BBD1, #3EEAEC)"
                  }
                  showPercentage={true}
                />
                <TradeLogMiniStatistics
                  title="Win Rate"
                  amount={`${stats.winRate.toFixed(1)}%`}
                  percentage={0}
                  backgroundGradient="linear-gradient(135deg, #38BBD1, #3EEAEC)"
                  showPercentage={false}
                />
                <TradeLogMiniStatistics
                  title="ROI"
                  amount={formatPercent(stats.roi)}
                  percentage={0}
                  backgroundGradient="linear-gradient(135deg, #38BBD1, #3EEAEC)"
                  showPercentage={false}
                />
                <TradeLogMiniStatistics
                  title="Active Trades"
                  amount={stats.activeTrades.toString()}
                  percentage={0}
                  backgroundGradient="linear-gradient(135deg, #38BBD1, #3EEAEC)"
                  showPercentage={false}
                />
              </SimpleGrid>
            </Box>

            <Divider borderColor={borderColor} />

            {/* Trading Activity */}
            <Box>
              <Text
                fontSize="xs"
                color={secondaryTextColor}
                fontWeight="medium"
                fontFamily="'IBM Plex Sans', sans-serif"
                textTransform="uppercase"
                mb={4}
              >
                Trading Activity
              </Text>
              <SimpleGrid columns={{ base: 1, sm: 2, md: 5 }} spacing={4}>
                <Box
                  bg={cardBg}
                  border="1px solid"
                  borderColor={borderColor}
                  borderRadius="lg"
                  p={4}
                >
                  <Text
                    fontSize="xs"
                    color={secondaryTextColor}
                    fontFamily="'IBM Plex Sans', sans-serif"
                    fontWeight="medium"
                    textTransform="uppercase"
                    mb={1}
                  >
                    Total Trades
                  </Text>
                  <Text
                    fontSize="xl"
                    color={textColor}
                    fontFamily="'IBM Plex Sans', sans-serif"
                    fontWeight="700"
                  >
                    {stats.member.totalTrades}
                  </Text>
                </Box>
                <Box
                  bg={cardBg}
                  border="1px solid"
                  borderColor={borderColor}
                  borderRadius="lg"
                  p={4}
                >
                  <Text
                    fontSize="xs"
                    color={secondaryTextColor}
                    fontFamily="'IBM Plex Sans', sans-serif"
                    fontWeight="medium"
                    textTransform="uppercase"
                    mb={1}
                  >
                    Closed Trades
                  </Text>
                  <Text
                    fontSize="xl"
                    color={textColor}
                    fontFamily="'IBM Plex Sans', sans-serif"
                    fontWeight="700"
                  >
                    {stats.closedTrades}
                  </Text>
                </Box>
                <Box
                  bg={cardBg}
                  border="1px solid"
                  borderColor={borderColor}
                  borderRadius="lg"
                  p={4}
                >
                  <Text
                    fontSize="xs"
                    color={secondaryTextColor}
                    fontFamily="'IBM Plex Sans', sans-serif"
                    fontWeight="medium"
                    textTransform="uppercase"
                    mb={1}
                  >
                    This Month
                  </Text>
                  <Text
                    fontSize="xl"
                    color={textColor}
                    fontFamily="'IBM Plex Sans', sans-serif"
                    fontWeight="700"
                  >
                    {stats.tradesThisMonth}
                  </Text>
                </Box>
                <Box
                  bg={cardBg}
                  border="1px solid"
                  borderColor={borderColor}
                  borderRadius="lg"
                  p={4}
                >
                  <Text
                    fontSize="xs"
                    color={secondaryTextColor}
                    fontFamily="'IBM Plex Sans', sans-serif"
                    fontWeight="medium"
                    textTransform="uppercase"
                    mb={1}
                  >
                    Avg Trade Size
                  </Text>
                  <Text
                    fontSize="xl"
                    color={textColor}
                    fontFamily="'IBM Plex Sans', sans-serif"
                    fontWeight="700"
                  >
                    {formatMoney(stats.averageTradeSize)}
                  </Text>
                </Box>
                <Box
                  bg={cardBg}
                  border="1px solid"
                  borderColor={borderColor}
                  borderRadius="lg"
                  p={4}
                >
                  <Text
                    fontSize="xs"
                    color={secondaryTextColor}
                    fontFamily="'IBM Plex Sans', sans-serif"
                    fontWeight="medium"
                    textTransform="uppercase"
                    mb={1}
                  >
                    Broker Status
                  </Text>
                  <Badge
                    bgGradient={
                      stats.brokerConnected
                        ? "linear-gradient(135deg, #06E363, #AAFAB1)"
                        : "linear-gradient(135deg, #E00101, #FE4244)"
                    }
                    color="white"
                    fontSize="xs"
                    fontFamily="'IBM Plex Sans', sans-serif"
                    fontWeight="medium"
                    textTransform="uppercase"
                    p="3px 10px"
                    borderRadius="8px"
                  >
                    {stats.brokerConnected ? "Connected" : "Not Connected"}
                  </Badge>
                </Box>
              </SimpleGrid>
            </Box>

            <Divider borderColor={borderColor} />

            {/* Performance Metrics */}
            <Box>
              <Text
                fontSize="xs"
                color={secondaryTextColor}
                fontWeight="medium"
                fontFamily="'IBM Plex Sans', sans-serif"
                textTransform="uppercase"
                mb={4}
              >
                Performance Metrics
              </Text>
              <SimpleGrid columns={{ base: 1, sm: 2, md: 4 }} spacing={4}>
                <Box
                  bg={cardBg}
                  border="1px solid"
                  borderColor={borderColor}
                  borderRadius="lg"
                  p={4}
                >
                  <Text
                    fontSize="xs"
                    color={secondaryTextColor}
                    fontFamily="'IBM Plex Sans', sans-serif"
                    fontWeight="medium"
                    textTransform="uppercase"
                    mb={1}
                  >
                    Best Trade
                  </Text>
                  <Text
                    fontSize="sm"
                    color={textColor}
                    fontFamily="'IBM Plex Sans', sans-serif"
                    fontWeight="600"
                    mb={1}
                  >
                    {stats.bestTrade.symbol}
                  </Text>
                  <Text
                    fontSize="lg"
                    fontFamily="'IBM Plex Sans', sans-serif"
                    fontWeight="700"
                    bgGradient="linear-gradient(135deg, #06E363, #AAFAB1)"
                    bgClip="text"
                    css={{
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    {formatMoney(stats.bestTrade.profit)}
                  </Text>
                  <Text
                    fontSize="xs"
                    color={secondaryTextColor}
                    fontFamily="'IBM Plex Sans', sans-serif"
                    mt={1}
                  >
                    {formatDate(stats.bestTrade.date)}
                  </Text>
                </Box>
                <Box
                  bg={cardBg}
                  border="1px solid"
                  borderColor={borderColor}
                  borderRadius="lg"
                  p={4}
                >
                  <Text
                    fontSize="xs"
                    color={secondaryTextColor}
                    fontFamily="'IBM Plex Sans', sans-serif"
                    fontWeight="medium"
                    textTransform="uppercase"
                    mb={1}
                  >
                    Worst Trade
                  </Text>
                  <Text
                    fontSize="sm"
                    color={textColor}
                    fontFamily="'IBM Plex Sans', sans-serif"
                    fontWeight="600"
                    mb={1}
                  >
                    {stats.worstTrade.symbol}
                  </Text>
                  <Text
                    fontSize="lg"
                    fontFamily="'IBM Plex Sans', sans-serif"
                    fontWeight="700"
                    bgGradient="linear-gradient(135deg, #E00101, #FE4244)"
                    bgClip="text"
                    css={{
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    {formatMoney(stats.worstTrade.loss)}
                  </Text>
                  <Text
                    fontSize="xs"
                    color={secondaryTextColor}
                    fontFamily="'IBM Plex Sans', sans-serif"
                    mt={1}
                  >
                    {formatDate(stats.worstTrade.date)}
                  </Text>
                </Box>
                <Box
                  bg={cardBg}
                  border="1px solid"
                  borderColor={borderColor}
                  borderRadius="lg"
                  p={4}
                >
                  <Text
                    fontSize="xs"
                    color={secondaryTextColor}
                    fontFamily="'IBM Plex Sans', sans-serif"
                    fontWeight="medium"
                    textTransform="uppercase"
                    mb={1}
                  >
                    Average Win
                  </Text>
                  <Text
                    fontSize="xl"
                    fontFamily="'IBM Plex Sans', sans-serif"
                    fontWeight="700"
                    bgGradient="linear-gradient(135deg, #06E363, #AAFAB1)"
                    bgClip="text"
                    css={{
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    {formatMoney(stats.averageWin)}
                  </Text>
                </Box>
                <Box
                  bg={cardBg}
                  border="1px solid"
                  borderColor={borderColor}
                  borderRadius="lg"
                  p={4}
                >
                  <Text
                    fontSize="xs"
                    color={secondaryTextColor}
                    fontFamily="'IBM Plex Sans', sans-serif"
                    fontWeight="medium"
                    textTransform="uppercase"
                    mb={1}
                  >
                    Average Loss
                  </Text>
                  <Text
                    fontSize="xl"
                    fontFamily="'IBM Plex Sans', sans-serif"
                    fontWeight="700"
                    bgGradient="linear-gradient(135deg, #E00101, #FE4244)"
                    bgClip="text"
                    css={{
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    {formatMoney(stats.averageLoss)}
                  </Text>
                </Box>
              </SimpleGrid>
              <SimpleGrid columns={{ base: 1, sm: 2, md: 4 }} spacing={4} mt={4}>
                <Box
                  bg={cardBg}
                  border="1px solid"
                  borderColor={borderColor}
                  borderRadius="lg"
                  p={4}
                >
                  <Text
                    fontSize="xs"
                    color={secondaryTextColor}
                    fontFamily="'IBM Plex Sans', sans-serif"
                    fontWeight="medium"
                    textTransform="uppercase"
                    mb={1}
                  >
                    Win/Loss Ratio
                  </Text>
                  <Text
                    fontSize="xl"
                    color={textColor}
                    fontFamily="'IBM Plex Sans', sans-serif"
                    fontWeight="700"
                  >
                    {stats.winLossRatio.toFixed(2)}
                  </Text>
                </Box>
                <Box
                  bg={cardBg}
                  border="1px solid"
                  borderColor={borderColor}
                  borderRadius="lg"
                  p={4}
                >
                  <Text
                    fontSize="xs"
                    color={secondaryTextColor}
                    fontFamily="'IBM Plex Sans', sans-serif"
                    fontWeight="medium"
                    textTransform="uppercase"
                    mb={1}
                  >
                    Current Streak
                  </Text>
                  <HStack spacing={2}>
                    <Text
                      fontSize="xl"
                      fontFamily="'IBM Plex Sans', sans-serif"
                      fontWeight="700"
                      bgGradient={
                        stats.currentStreak.type === "win"
                          ? "linear-gradient(135deg, #06E363, #AAFAB1)"
                          : "linear-gradient(135deg, #E00101, #FE4244)"
                      }
                      bgClip="text"
                      css={{
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                      }}
                    >
                      {stats.currentStreak.count}
                    </Text>
                    <Badge
                      bgGradient={
                        stats.currentStreak.type === "win"
                          ? "linear-gradient(135deg, #06E363, #AAFAB1)"
                          : "linear-gradient(135deg, #E00101, #FE4244)"
                      }
                      color="white"
                      fontSize="xs"
                      fontFamily="'IBM Plex Sans', sans-serif"
                      fontWeight="medium"
                      textTransform="uppercase"
                      p="3px 10px"
                      borderRadius="8px"
                    >
                      {stats.currentStreak.type === "win" ? "Win" : "Loss"}
                    </Badge>
                  </HStack>
                </Box>
                <Box
                  bg={cardBg}
                  border="1px solid"
                  borderColor={borderColor}
                  borderRadius="lg"
                  p={4}
                >
                  <Text
                    fontSize="xs"
                    color={secondaryTextColor}
                    fontFamily="'IBM Plex Sans', sans-serif"
                    fontWeight="medium"
                    textTransform="uppercase"
                    mb={1}
                  >
                    Longest Win Streak
                  </Text>
                  <Text
                    fontSize="xl"
                    fontFamily="'IBM Plex Sans', sans-serif"
                    fontWeight="700"
                    bgGradient="linear-gradient(135deg, #06E363, #AAFAB1)"
                    bgClip="text"
                    css={{
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    {stats.longestWinStreak}
                  </Text>
                </Box>
                <Box
                  bg={cardBg}
                  border="1px solid"
                  borderColor={borderColor}
                  borderRadius="lg"
                  p={4}
                >
                  <Text
                    fontSize="xs"
                    color={secondaryTextColor}
                    fontFamily="'IBM Plex Sans', sans-serif"
                    fontWeight="medium"
                    textTransform="uppercase"
                    mb={1}
                  >
                    Longest Loss Streak
                  </Text>
                  <Text
                    fontSize="xl"
                    fontFamily="'IBM Plex Sans', sans-serif"
                    fontWeight="700"
                    bgGradient="linear-gradient(135deg, #E00101, #FE4244)"
                    bgClip="text"
                    css={{
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    {stats.longestLossStreak}
                  </Text>
                </Box>
              </SimpleGrid>
            </Box>

            <Divider borderColor={borderColor} />

            {/* Portfolio Metrics */}
            <Box>
              <Text
                fontSize="xs"
                color={secondaryTextColor}
                fontWeight="medium"
                fontFamily="'IBM Plex Sans', sans-serif"
                textTransform="uppercase"
                mb={4}
              >
                Portfolio Metrics
              </Text>
              <SimpleGrid columns={{ base: 1, sm: 2, md: 5 }} spacing={4}>
                <Box
                  bg={cardBg}
                  border="1px solid"
                  borderColor={borderColor}
                  borderRadius="lg"
                  p={4}
                >
                  <Text
                    fontSize="xs"
                    color={secondaryTextColor}
                    fontFamily="'IBM Plex Sans', sans-serif"
                    fontWeight="medium"
                    textTransform="uppercase"
                    mb={1}
                  >
                    Portfolio Size
                  </Text>
                  <Text
                    fontSize="xl"
                    color={textColor}
                    fontFamily="'IBM Plex Sans', sans-serif"
                    fontWeight="700"
                  >
                    {formatMoney(stats.member.portfolioSize)}
                  </Text>
                </Box>
                <Box
                  bg={cardBg}
                  border="1px solid"
                  borderColor={borderColor}
                  borderRadius="lg"
                  p={4}
                >
                  <Text
                    fontSize="xs"
                    color={secondaryTextColor}
                    fontFamily="'IBM Plex Sans', sans-serif"
                    fontWeight="medium"
                    textTransform="uppercase"
                    mb={1}
                  >
                    Total Invested
                  </Text>
                  <Text
                    fontSize="xl"
                    color={textColor}
                    fontFamily="'IBM Plex Sans', sans-serif"
                    fontWeight="700"
                  >
                    {formatMoney(stats.totalInvested)}
                  </Text>
                </Box>
                <Box
                  bg={cardBg}
                  border="1px solid"
                  borderColor={borderColor}
                  borderRadius="lg"
                  p={4}
                >
                  <Text
                    fontSize="xs"
                    color={secondaryTextColor}
                    fontFamily="'IBM Plex Sans', sans-serif"
                    fontWeight="medium"
                    textTransform="uppercase"
                    mb={1}
                  >
                    Profit Factor
                  </Text>
                  <Text
                    fontSize="xl"
                    color={textColor}
                    fontFamily="'IBM Plex Sans', sans-serif"
                    fontWeight="700"
                  >
                    {stats.profitFactor.toFixed(2)}
                  </Text>
                </Box>
                <Box
                  bg={cardBg}
                  border="1px solid"
                  borderColor={borderColor}
                  borderRadius="lg"
                  p={4}
                >
                  <Text
                    fontSize="xs"
                    color={secondaryTextColor}
                    fontFamily="'IBM Plex Sans', sans-serif"
                    fontWeight="medium"
                    textTransform="uppercase"
                    mb={1}
                  >
                    Sharpe Ratio
                  </Text>
                  <Text
                    fontSize="xl"
                    color={textColor}
                    fontFamily="'IBM Plex Sans', sans-serif"
                    fontWeight="700"
                  >
                    {stats.sharpeRatio !== null
                      ? stats.sharpeRatio.toFixed(2)
                      : "N/A"}
                  </Text>
                </Box>
                <Box
                  bg={cardBg}
                  border="1px solid"
                  borderColor={borderColor}
                  borderRadius="lg"
                  p={4}
                >
                  <Text
                    fontSize="xs"
                    color={secondaryTextColor}
                    fontFamily="'IBM Plex Sans', sans-serif"
                    fontWeight="medium"
                    textTransform="uppercase"
                    mb={1}
                  >
                    Max Drawdown
                  </Text>
                  <Text
                    fontSize="xl"
                    fontFamily="'IBM Plex Sans', sans-serif"
                    fontWeight="700"
                    bgGradient="linear-gradient(135deg, #E00101, #FE4244)"
                    bgClip="text"
                    css={{
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    {formatPercent(stats.maxDrawdown)}
                  </Text>
                </Box>
              </SimpleGrid>
            </Box>

            {/* Recent Trades */}
            {stats.recentTrades.length > 0 && (
              <>
                <Divider borderColor={borderColor} />
                <Box>
                  <Text
                    fontSize="xs"
                    color={secondaryTextColor}
                    fontWeight="medium"
                    fontFamily="'IBM Plex Sans', sans-serif"
                    textTransform="uppercase"
                    mb={4}
                  >
                    Recent Trades
                  </Text>
                  <Box
                    bg={cardBg}
                    border="1px solid"
                    borderColor={borderColor}
                    borderRadius="lg"
                    overflow="hidden"
                  >
                    <Table variant="simple" size="sm">
                      <Thead bg={cardBg}>
                        <Tr>
                          <Th
                            color={secondaryTextColor}
                            fontFamily="'IBM Plex Sans', sans-serif"
                            fontWeight="medium"
                            fontSize="xs"
                            textTransform="uppercase"
                            borderColor={borderColor}
                          >
                            Symbol
                          </Th>
                          <Th
                            color={secondaryTextColor}
                            fontFamily="'IBM Plex Sans', sans-serif"
                            fontWeight="medium"
                            fontSize="xs"
                            textTransform="uppercase"
                            borderColor={borderColor}
                          >
                            Date
                          </Th>
                          <Th
                            color={secondaryTextColor}
                            fontFamily="'IBM Plex Sans', sans-serif"
                            fontWeight="medium"
                            fontSize="xs"
                            textTransform="uppercase"
                            borderColor={borderColor}
                            isNumeric
                          >
                            P&L
                          </Th>
                          <Th
                            color={secondaryTextColor}
                            fontFamily="'IBM Plex Sans', sans-serif"
                            fontWeight="medium"
                            fontSize="xs"
                            textTransform="uppercase"
                            borderColor={borderColor}
                            isNumeric
                          >
                            Return
                          </Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {stats.recentTrades.map((trade) => {
                          const isProfit = trade.pnl > 0;
                          const isLoss = trade.pnl < 0;

                          return (
                            <Tr key={trade.id}>
                              <Td borderColor={borderColor}>
                                <Text
                                  fontSize="sm"
                                  color={textColor}
                                  fontFamily="'IBM Plex Sans', sans-serif"
                                  fontWeight="medium"
                                >
                                  {trade.symbol}
                                </Text>
                              </Td>
                              <Td borderColor={borderColor}>
                                <Text
                                  fontSize="sm"
                                  color={secondaryTextColor}
                                  fontFamily="'IBM Plex Sans', sans-serif"
                                  fontWeight="medium"
                                >
                                  {formatDate(trade.date)}
                                </Text>
                              </Td>
                              <Td borderColor={borderColor} isNumeric>
                                <Text
                                  fontSize="sm"
                                  fontFamily="'IBM Plex Sans', sans-serif"
                                  fontWeight="medium"
                                  bgGradient={
                                    isProfit
                                      ? "linear-gradient(135deg, #06E363, #AAFAB1)"
                                      : isLoss
                                      ? "linear-gradient(135deg, #E00101, #FE4244)"
                                      : "gray.500"
                                  }
                                  bgClip="text"
                                  css={{
                                    WebkitBackgroundClip: "text",
                                    WebkitTextFillColor: "transparent",
                                  }}
                                >
                                  {formatMoney(trade.pnl)}
                                </Text>
                              </Td>
                              <Td borderColor={borderColor} isNumeric>
                                <Text
                                  fontSize="sm"
                                  fontFamily="'IBM Plex Sans', sans-serif"
                                  fontWeight="medium"
                                  bgGradient={
                                    isProfit
                                      ? "linear-gradient(135deg, #06E363, #AAFAB1)"
                                      : isLoss
                                      ? "linear-gradient(135deg, #E00101, #FE4244)"
                                      : "gray.500"
                                  }
                                  bgClip="text"
                                  css={{
                                    WebkitBackgroundClip: "text",
                                    WebkitTextFillColor: "transparent",
                                  }}
                                >
                                  {formatPercent(trade.returnPercent)}
                                </Text>
                              </Td>
                            </Tr>
                          );
                        })}
                      </Tbody>
                    </Table>
                  </Box>
                </Box>
              </>
            )}
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default MemberStatsModal;

